import React from 'react';
import {
  Truck, PackageCheck, Loader2, Download, ChevronRight,
  CheckCircle2, FileText, Camera, PenLine, Circle, Clock,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(d); }
};

const computeStatus = (note) => {
  if (!note) return { key: 'draft', label: 'Brouillon', color: 'slate' };
  const sigs = (note.signatures || []).length;
  if (sigs >= 2) {
    if (note.type === 'unloading' && (note.merchandiseChecks || []).some(c => ['non_conform', 'not_verifiable'].includes(c.status))) {
      return { key: 'with_reserve', label: 'Avec réserve', color: 'orange' };
    }
    return { key: 'complete', label: 'Complet', color: 'emerald' };
  }
  return { key: 'in_progress', label: 'En cours', color: 'emerald' };
};

const NoteCard = ({ type, note, onStart, onDownload, downloading = false }) => {
  const isLoading = type === 'loading';
  const Icon = isLoading ? Truck : PackageCheck;
  const title = isLoading ? 'Bon de chargement' : 'Bon de déchargement';
  const description = isLoading
    ? 'Prouve que la marchandise a bien été prise en charge au départ.'
    : 'Prouve la réception de la marchandise et signale toute réserve.';
  const status = computeStatus(note);
  const isComplete = status.key === 'complete' || status.key === 'with_reserve';
  const sigCount = (note?.signatures || []).length;
  const photoCount = (note?.photos || []).length;
  const checkCount = (note?.merchandiseChecks || []).length;

  // Étapes du parcours visualisées
  const steps = isLoading
    ? [
        { label: 'Vérification', done: !!note?.id },
        { label: 'Détails', done: !!(note?.endTime || note?.startTime) },
        { label: 'Photos', done: photoCount >= 2 },
        { label: 'Signatures', done: sigCount >= 2 },
      ]
    : [
        { label: 'Vérification', done: !!note?.id },
        { label: 'Contrôle marchandise', done: checkCount >= 4 },
        { label: 'Réserves', done: checkCount >= 4 },
        { label: 'Photos', done: photoCount >= 2 },
        { label: 'Signatures', done: sigCount >= 2 },
      ];
  const doneCount = steps.filter(s => s.done).length;
  const progressPct = Math.round((doneCount / steps.length) * 100);

  const colorMap = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'text-emerald-500' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'text-orange-500' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'text-slate-400' },
  };
  const c = colorMap[status.color];

  return (
    <Card className={`overflow-hidden transition-all ${isComplete ? 'border-emerald-300 shadow-sm' : 'border-slate-200 hover:border-emerald-200 hover:shadow-md'}`}
          data-testid={`note-card-${type}`}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-5 flex items-start gap-4 border-b border-slate-100">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
            isComplete ? 'bg-emerald-600' : 'bg-emerald-100'
          }`}>
            <Icon className={`w-7 h-7 ${isComplete ? 'text-white' : 'text-emerald-700'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 leading-tight">{title}</h3>
                {note?.noteNumber && (
                  <div className="text-xs font-mono text-slate-500 mt-0.5">{note.noteNumber}</div>
                )}
              </div>
              <Badge className={`${c.bg} ${c.text} ${c.border} text-xs font-medium border whitespace-nowrap`}>
                <Circle className={`w-2 h-2 mr-1.5 fill-current ${c.dot}`} />
                {status.label}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Progression */}
        <div className="px-5 py-4 bg-slate-50/40 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-slate-600 uppercase tracking-wide">Progression</span>
            <span className="text-xs font-semibold text-slate-700">{doneCount} / {steps.length} étapes</span>
          </div>
          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden mb-3">
            <div
              className={`h-full transition-all ${isComplete ? 'bg-emerald-500' : 'bg-emerald-400'}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {steps.slice(0, 4).map((s, i) => (
              <div key={i} className={`flex items-center gap-1 text-[11px] ${s.done ? 'text-emerald-700' : 'text-slate-400'}`}>
                {s.done
                  ? <CheckCircle2 className="w-3 h-3 shrink-0" />
                  : <Circle className="w-3 h-3 shrink-0" />
                }
                <span className="truncate">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats compactes */}
        {note && (
          <div className="px-5 py-3 grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
            <Stat icon={PenLine} label="Signatures" value={`${sigCount}/2`} done={sigCount >= 2} />
            <Stat icon={Camera} label="Photos" value={`${photoCount}`} done={photoCount >= 2} />
            {isLoading
              ? <Stat icon={Clock} label="Mise à jour" value={note?.updatedAt ? fmtDateTime(note.updatedAt).split(' ')[1] : '—'} done={false} muted />
              : <Stat icon={FileText} label="Contrôles" value={`${checkCount}/4`} done={checkCount >= 4} />
            }
          </div>
        )}

        {/* Action */}
        <div className="p-5">
          {isComplete ? (
            <div className="space-y-2">
              <Button onClick={onDownload} disabled={downloading}
                      className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`download-${type}-btn`}>
                {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Télécharger le PDF signé
              </Button>
              <Button variant="ghost" onClick={onStart}
                      className="w-full h-9 text-xs text-slate-600 hover:text-slate-800"
                      data-testid={`view-${type}-btn`}>
                Consulter le détail
              </Button>
            </div>
          ) : (
            <Button onClick={onStart}
                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`start-${type}-btn`}>
              {note?.id ? 'Continuer la signature' : 'Démarrer la signature'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ icon: Icon, label, value, done, muted }) => (
  <div className="flex flex-col items-center text-center px-2">
    <Icon className={`w-3.5 h-3.5 mb-1 ${done ? 'text-emerald-600' : muted ? 'text-slate-400' : 'text-slate-400'}`} />
    <div className={`text-sm font-semibold ${done ? 'text-emerald-700' : 'text-slate-700'}`}>{value}</div>
    <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
  </div>
);

/**
 * Page de choix du type de bon à signer.
 * Affichée après le login public, avant l'entrée dans le wizard.
 *
 * Pour chaque bon :
 *  - Statut dynamique (Brouillon, En cours, Complet, Avec réserve)
 *  - Progression des étapes
 *  - Stats compactes (signatures, photos, contrôles)
 *  - Bouton "Démarrer/Continuer la signature" OU "Télécharger le PDF" si signé
 */
const NoteTypeSelector = ({ notes = [], operation, onSelect, onDownload, downloadingType }) => {
  const loadingNote = notes.find(n => n.type === 'loading') || null;
  const unloadingNote = notes.find(n => n.type === 'unloading') || null;

  return (
    <div className="space-y-6" data-testid="note-type-selector">
      <div className="text-center max-w-2xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Quel bon souhaitez-vous traiter ?</h2>
        <p className="text-sm text-slate-500 mt-2">
          Sélectionnez ci-dessous le bon que vous souhaitez signer.
          Si un bon est déjà finalisé, vous pouvez le télécharger directement.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl mx-auto">
        <NoteCard
          type="loading"
          note={loadingNote}
          onStart={() => onSelect('loading')}
          onDownload={() => onDownload('loading', loadingNote)}
          downloading={downloadingType === 'loading'}
        />
        <NoteCard
          type="unloading"
          note={unloadingNote}
          onStart={() => onSelect('unloading')}
          onDownload={() => onDownload('unloading', unloadingNote)}
          downloading={downloadingType === 'unloading'}
        />
      </div>

      {/* Récap global si les 2 bons sont signés */}
      {loadingNote && unloadingNote &&
       (loadingNote.signatures || []).length >= 2 &&
       (unloadingNote.signatures || []).length >= 2 && (
        <Card className="bg-emerald-50 border-emerald-200 max-w-4xl mx-auto">
          <CardContent className="p-4 flex items-center gap-3 text-sm text-emerald-800">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            <div>
              <strong>Mission complète.</strong> Les 2 bons sont signés et le statut de l'opération
              {operation?.reference ? <span className="font-mono mx-1">{operation.reference}</span> : ''} a été
              mis à jour automatiquement. L'admin a été notifié.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NoteTypeSelector;
